import { TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '@features/auth/services/auth.service';
import { CompanyService } from '@features/company/services/company.service';
import { FilesService } from '@core/services/files.service';

const ABSOLUTE_URL_PATTERN = /^(https?:|data:|blob:)/i;

/**
 * @description
 * Bloque de información del usuario y la empresa activa dentro del menú de navegación.
 * Debajo del breakpoint definido en `menu-info-user.scss` se muestra solo un avatar
 * con las iniciales del usuario (nombre y empresa disponibles vía tooltip); a partir
 * de ese punto se muestra el diseño completo con icono, nombre, empresa y logo.
 */
@Component({
  selector: 'app-menu-info-user',
  imports: [MatIconModule, MatTooltipModule, TitleCasePipe],
  templateUrl: './menu-info-user.html',
  styleUrl: './menu-info-user.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuInfoUserComponent {

  readonly _authService = inject(AuthService);
  readonly _companyService = inject(CompanyService);
  readonly _filesService = inject(FilesService);

  /** Usuario de la sesión activa, reactivo a cambios de sesión. */
  readonly user = this._authService.user;
  /** Empresa activa, reactiva a cambios como la actualización del logo. */
  readonly business = this._companyService.activeBusiness;

  /** URL del logo de la empresa, resuelta contra el blob storage cuando `logoFileName` no es ya una URL absoluta. */
  readonly logoUrl = computed(() => {
    const logoFileName = this.business()?.logoFileName;
    if (!logoFileName) return null;
    if (ABSOLUTE_URL_PATTERN.test(logoFileName)) return logoFileName;
    return `${this._filesService.blobUrl}/files/${logoFileName}`;
  });

  /** Iniciales del usuario para el avatar; usadas cuando aún no hay foto de perfil. */
  readonly initials = computed(() => {
    const first = this.user()?.firstName?.trim()?.charAt(0) ?? '';
    const last = this.user()?.lastName?.trim()?.charAt(0) ?? '';
    return `${first}${last}`.toUpperCase() || '?';
  });

  /** Texto de respaldo (nombre + empresa) mostrado en tooltip cuando el texto se trunca. */
  readonly tooltipText = computed(() => {
    const name = [this.user()?.firstName, this.user()?.lastName]
      .filter((part): part is string => !!part?.trim())
      .join(' ');
    const businessName = this.business()?.name;
    return businessName ? `${name} · ${businessName}` : name;
  });
}
